import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Copy, Check, Mail, AlertTriangle, MoreHorizontal, Eye, Edit, Server, CheckCircle2, Clock, XCircle, Settings, Globe, RefreshCw } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DataTable } from '@/components/data/DataTable'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/app/store/auth'
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat'
import { GlobalMap } from '@/components/maps/GlobalMap'
import { toMapLocations, toMapVaults, toVaultCopies, type ApiMapNode, type ApiMapConnection } from '@/components/maps/mapAdapters'
import { TranslatableText } from '@/components/ui/TranslatableText'
import { ShowMeSchedulerDiagramButton } from '@/components/diagrams/SchedulerConnectionDiagramDialog'
import { vaultsApi, type VaultItem, type VaultRegion, type ActiveScheduler, type SetupSchedulerResponse } from '../api'
import { adminApi } from '@/features/admin/api'

function copyToClipboard(text: string): boolean {
    try {
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text)
            return true
        }
    } catch { /* fallback below */ }
    try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        return true
    } catch { return false }
}
import type { ColumnDef } from '@tanstack/react-table'
import { provisioningState } from '../provisioning'
import { useCan } from '@/hooks/useCan'

function StatusBadge({ status }: { status: string }) {
    const { t } = useTranslation()
    const config: Record<string, { bg: string; text: string; icon: typeof CheckCircle2 }> = {
        Active: { bg: 'bg-green-100 dark:bg-green-950/30', text: 'text-green-700 dark:text-green-400', icon: CheckCircle2 },
        Provisioning: { bg: 'bg-blue-100 dark:bg-blue-950/30', text: 'text-blue-700 dark:text-blue-400', icon: Clock },
        Pending: { bg: 'bg-amber-100 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', icon: Clock },
        Failed: { bg: 'bg-red-100 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-400', icon: XCircle },
        Decommissioned: { bg: 'bg-gray-100 dark:bg-gray-950/30', text: 'text-gray-700 dark:text-gray-400', icon: XCircle },
    }
    const labels: Record<string, string> = {
        Active: t('vaults.statusActive', 'Active'),
        Provisioning: t('vaults.statusProvisioning', 'Provisioning'),
        Pending: t('vaults.statusPending', 'Pending'),
        Failed: t('vaults.statusFailed', 'Failed'),
        Decommissioned: t('vaults.statusDecommissioned', 'Decommissioned'),
    }
    const c = config[status] || config.Active
    const Icon = c.icon
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
            <Icon className="h-3 w-3" />{labels[status] || status}
        </span>
    )
}

/**
 * Status, plus what "Provisioning" is actually doing.
 *
 * The badge alone cannot distinguish a vault Terraform is busy building from one nothing has
 * even asked for yet — and the second is a fault, not a wait. It is what the estate did before
 * the emitter existed: the row sat at Provisioning indefinitely and the screen looked identical
 * to a healthy build.
 */
function VaultStatusCell({ v }: { v: VaultItem }) {
    const { t } = useTranslation()
    const p = provisioningState(v)

    if (p.phase === 'failed') {
        return (
            <div className="flex flex-col gap-0.5 min-w-0">
                <StatusBadge status="Failed" />
                {p.error && (
                    <span className="text-[11px] text-red-600 dark:text-red-400 truncate max-w-[220px]" title={p.error}>{p.error}</span>
                )}
            </div>
        )
    }

    if (p.phase === 'none') return <StatusBadge status={v.operationalStatus} />

    const caption = p.phase === 'queued'
        ? t('vaults.buildQueued', 'Queued — not yet requested')
        : p.phase === 'waiting'
            ? t('vaults.buildWaitingRegion', 'Waiting for its region')
            : p.stageNumber && p.stageCount
                ? `${p.stage || t('vaults.buildBuilding', 'Building')} · ${p.stageNumber}/${p.stageCount}`
                : p.stage || t('vaults.buildBuilding', 'Building')

    return (
        <div className="flex flex-col gap-0.5 min-w-0">
            <StatusBadge status="Provisioning" />
            <span className={`text-[11px] truncate max-w-[220px] ${p.stalled ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-muted-foreground'}`}
                  title={p.stalled ? t('vaults.buildStalledHint', 'No progress for over ten minutes. If it is still queued, nothing is emitting the build request.') : caption}>
                {caption}{p.stalled ? ` — ${t('vaults.buildStalled', 'no progress for 10+ min')}` : ''}
            </span>
        </div>
    )
}

function formatSize(bytes: number): string {
    if (!bytes) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`
}

export function VaultListPage() {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    const { formatDateTime } = useDateTimeFormat()
    // The screen is open to every vaults.read; a change is offered only where the API allows it.
    // Setting up a scheduler's connection token is a change to the vault: vaults.update.
    const canCreate = useCan('vaults.create')
    const canUpdate = useCan('vaults.update')

    const [showCreate, setShowCreate] = useState(false)
    const [createForm, setCreateForm] = useState({ description: '', region: '', retention: 30 })
    const [showView, setShowView] = useState<VaultItem | null>(null)
    const [showEdit, setShowEdit] = useState<VaultItem | null>(null)
    const [editForm, setEditForm] = useState({ description: '', analytics: false, retention: 30 })
    const [showSetup, setShowSetup] = useState<VaultItem | null>(null)
    const [selectedScheduler, setSelectedScheduler] = useState('')
    const [showToken, setShowToken] = useState<SetupSchedulerResponse | null>(null)
    const [copied, setCopied] = useState(false)
    const [tokenSaved, setTokenSaved] = useState(false)
    const [showTokenWarning, setShowTokenWarning] = useState(false)

    // Fetch vaults
    const { data: vaultsData, isLoading } = useQuery({
        queryKey: ['vaults'],
        queryFn: async () => {
            const res = await vaultsApi.list()
            const d = res.data as any
            return (d?.data || []) as VaultItem[]
        },
        enabled: isAuthenticated,
    })

    // Fetch regions for create dialog
    const { data: regionsData } = useQuery({
        queryKey: ['vault-regions'],
        queryFn: async () => {
            const res = await vaultsApi.getRegions()
            const d = res.data as any
            return (d?.data || []) as VaultRegion[]
        },
        enabled: isAuthenticated && showCreate,
    })

    // Fetch vault map data


    // Fetch paths data (locations + vaults + connections)
    const { data: pathsData } = useQuery({
        queryKey: ['vault-paths'],
        queryFn: async () => {
            const res = await (await import('@/lib/api/axios')).default.get('/api/vaults/paths')
            const d = (res.data as any)?.data
            return d as { locations: ApiMapNode[]; connections: ApiMapConnection[] }
        },
        enabled: isAuthenticated,
    })

    const pathLocations = useMemo(() => toMapLocations(pathsData?.locations), [pathsData])
    /** Edges are off by default: on a busy estate they crowd the markers. */
    const [showEdges, setShowEdges] = useState(false)

    const pathCopies = useMemo(() => toVaultCopies(pathsData?.connections), [pathsData])

    /**
     * Vault markers come from the PATHS payload, not from map/data.
     *
     * The connections reference `vault-{VaultID}`, and map/data keys vaults differently, so
     * drawing markers from map/data left every edge with an endpoint the map could not find —
     * and the component skips those silently. map/data is still fetched for the table.
     */
    const vaultMarkers = useMemo(() => toMapVaults(pathsData?.locations), [pathsData])

    // Fetch active schedulers for setup dialog
    const { data: schedulersData } = useQuery({
        queryKey: ['active-schedulers'],
        queryFn: async () => {
            const res = await vaultsApi.getActiveSchedulers()
            const d = res.data as any
            return (d?.data || []) as ActiveScheduler[]
        },
        enabled: isAuthenticated && !!showSetup,
    })

    const vaults = vaultsData || []
    const regions = regionsData || []

    // Configured default vault retention (from System Settings) — pre-fills the create form, so it
    // is asked only by those who can create one (the route: system.admin, vaults.create).
    const { data: retentionDefaults } = useQuery({
        queryKey: ['retention-defaults'],
        queryFn: async () => {
            const res = await adminApi.getRetentionDefaults()
            return res.data.data
        },
        enabled: canCreate,
    })
    const defaultVaultRetention = retentionDefaults?.vault ?? 30
    const schedulers = schedulersData || []

    // Create vault
    const createMut = useMutation({
        mutationFn: () => vaultsApi.create({
            description: createForm.description,
            region: createForm.region,
            retention: createForm.retention,
        }),
        onSuccess: () => {
            setShowCreate(false)
            setCreateForm({ description: '', region: '', retention: defaultVaultRetention })
            queryClient.invalidateQueries({ queryKey: ['vaults'] })
            toast({
                title: t('vaults.createRequestedTitle', 'Vault creation requested'),
                description: t('vaults.createRequestedDescription', 'The vault will be provisioned — this takes a few minutes.'),
            })
        },
        onError: () => toast({ title: t('vaults.createError', 'Failed to create vault'), variant: 'destructive' }),
    })

    // Update vault
    const updateMut = useMutation({
        mutationFn: ({ id, data }: { id: number; data: Parameters<typeof vaultsApi.update>[1] }) =>
            vaultsApi.update(id, data),
        onSuccess: () => {
            setShowEdit(null)
            queryClient.invalidateQueries({ queryKey: ['vaults'] })
            toast({ title: t('vaults.updateSuccess', 'Vault updated') })
        },
        onError: () => toast({ title: t('vaults.updateError', 'Failed to update vault'), variant: 'destructive' }),
    })

    // Setup scheduler token
    const setupMut = useMutation({
        mutationFn: ({ vaultId, schedulerId }: { vaultId: number; schedulerId: string }) =>
            vaultsApi.setupScheduler(vaultId, schedulerId),
        onSuccess: (res) => {
            const d = (res.data as any)?.data || res.data
            setShowSetup(null)
            setSelectedScheduler('')
            setShowToken(d as SetupSchedulerResponse)
            setCopied(false)
            setTokenSaved(false)
            setShowTokenWarning(false)
        },
        onError: () => toast({ title: t('vaults.tokenError', 'Failed to generate token'), variant: 'destructive' }),
    })

    const columns: ColumnDef<VaultItem>[] = [
        {
            accessorKey: 'vaultId',
            header: t('vaults.vaultId', 'Vault ID'),
            cell: ({ row }) => <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{row.original.vaultId}</code>,
        },
        {
            accessorKey: 'description',
            header: t('vaults.description', 'Description'),
            cell: ({ row }) => (
                <div className="text-sm font-medium">{row.original.description ? <TranslatableText text={row.original.description} /> : '-'}</div>
            ),
        },
        {
            accessorKey: 'regionName',
            header: t('vaults.region', 'Region'),
            size: 220,
            cell: ({ row }) => (
                <div className="flex items-center gap-1.5">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm">{row.original.regionName ? <TranslatableText text={row.original.regionName} /> : '-'}</span>
                </div>
            ),
        },
        {
            accessorKey: 'operationalStatus',
            header: t('vaults.status', 'Status'),
            cell: ({ row }) => <VaultStatusCell v={row.original} />,
        },
        {
            accessorKey: 'objectCount',
            header: t('vaults.objects', 'Objects'),
            size: 80,
            cell: ({ row }) => <span className="text-sm">{(row.original.objectCount || 0).toLocaleString()}</span>,
        },
        {
            accessorKey: 'versionCount',
            header: t('vaults.versions', 'Versions'),
            size: 80,
            cell: ({ row }) => <span className="text-sm">{(row.original.versionCount || 0).toLocaleString()}</span>,
        },
        {
            accessorKey: 'size',
            header: t('vaults.size', 'Size'),
            size: 80,
            cell: ({ row }) => <span className="text-sm">{formatSize(row.original.size)}</span>,
        },
        {
            accessorKey: 'retentionDays',
            header: t('vaults.retention', 'Retention'),
            size: 90,
            cell: ({ row }) => row.original.retentionDays != null
                ? <span className="text-sm">{t('vaults.daysValue', '{{days}} days', { days: row.original.retentionDays })}</span>
                : <span className="text-sm text-muted-foreground">-</span>,
        },
        {
            accessorKey: 'created',
            header: t('vaults.created', 'Created'),
            cell: ({ row }) => row.original.created
                ? <span className="text-sm">{formatDateTime(row.original.created)}</span>
                : <span className="text-sm text-muted-foreground">-</span>,
        },
        {
            id: 'actions',
            header: '',
            size: 50,
            cell: ({ row }) => {
                const v = row.original
                return (
                    <div className="flex justify-end">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => setShowView(v)}>
                                    <Eye className="mr-2 h-4 w-4" />{t('vaults.viewDetails', 'View Details')}
                                </DropdownMenuItem>
                                {canUpdate && (
                                    <DropdownMenuItem onClick={() => {
                                        setShowEdit(v)
                                        setEditForm({
                                            description: v.description,
                                            analytics: v.analytics,
                                            retention: v.retentionDays ?? defaultVaultRetention,
                                        })
                                    }}>
                                        <Edit className="mr-2 h-4 w-4" />{t('vaults.editVault', 'Edit Vault')}
                                    </DropdownMenuItem>
                                )}
                                {canUpdate && v.operationalStatus === 'Active' && (
                                    <DropdownMenuItem onClick={() => { setShowSetup(v); setSelectedScheduler('') }}>
                                        <Settings className="mr-2 h-4 w-4" />{t('vaults.setupScheduler', 'Setup Scheduler')}
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )
            },
        },
    ]

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">{t('vaults.title', 'Vaults')}</h1>
                    <p className="text-muted-foreground">{t('vaults.pageDescription', 'Manage vaults')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <ShowMeSchedulerDiagramButton />
                    {canCreate && (
                        <Button onClick={() => { setCreateForm({ description: '', region: '', retention: defaultVaultRetention }); setShowCreate(true) }}>
                            <Plus className="h-4 w-4 mr-2" />{t('vaults.createVault', 'Create Vault')}
                        </Button>
                    )}
                </div>
            </div>

            {/* Map */}
            {/* Vaults screen: the vault markers come from the map endpoint, and the paths
                payload supplies the locations they serve plus the edges between them. */}
            <div className="flex items-center justify-end">
                <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                    <input
                        type="checkbox"
                        checked={showEdges}
                        onChange={(e) => setShowEdges(e.target.checked)}
                        className="h-3.5 w-3.5 cursor-pointer accent-primary"
                    />
                    {t('vaults.showVaultCopies', 'Show vault copies')}
                </label>
            </div>
            <GlobalMap
                vaults={vaultMarkers}
                locations={pathLocations}
                copies={showEdges ? pathCopies : []}
                height={360}
            />

            {isLoading ? (
                <div className="flex items-center justify-center h-32 text-muted-foreground">{t('common.loading', 'Loading...')}</div>
            ) : vaults.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 border rounded-lg bg-muted/30">
                    <Server className="h-10 w-10 text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">{t('vaults.noVaults', 'No vaults configured')}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t('vaults.noVaultsHint', 'Create a vault to start storing backups')}</p>
                    {/* Same trigger as the header — see the note on the Schedulers screen. Click
                        to open; nothing auto-opens. */}
                    <ShowMeSchedulerDiagramButton size="sm" className="mt-3" />
                </div>
            ) : (
                <DataTable columns={columns} data={vaults} searchKey="description" searchPlaceholder={t('vaults.searchPlaceholder', 'Search vaults...')} />
            )}

            {/* Create Vault Dialog */}
            {showCreate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">{t('vaults.createVault', 'Create Vault')}</h3>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm">{t('vaults.description', 'Description')}</Label>
                                <Input
                                    value={createForm.description}
                                    onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                                    placeholder={t('vaults.descriptionPlaceholder', 'e.g. UK Production Vault, US DR Vault')}
                                    className="text-sm mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-sm">{t('vaults.region', 'Region')}</Label>
                                <select
                                    value={createForm.region}
                                    onChange={(e) => setCreateForm(f => ({ ...f, region: e.target.value }))}
                                    className="w-full mt-1 rounded-md border bg-background px-3 py-2 text-sm"
                                >
                                    <option value="">{t('vaults.selectRegion', 'Select region...')}</option>
                                    {regions.map(r => (
                                        <option key={r.code} value={r.code}>
                                            {r.city || r.name}{r.country ? `, ${r.country}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label className="text-sm">{t('vaults.retentionDaysLabel', 'Retention (days)')}</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    value={createForm.retention}
                                    onChange={(e) => setCreateForm(f => ({ ...f, retention: Number(e.target.value) }))}
                                    className="text-sm mt-1"
                                />
                                <p className="text-xs text-muted-foreground mt-1">{t('vaults.retentionHintDefault', 'Number of days to retain backups in this vault. Default is set in System Settings.')}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 mt-4">
                            <Clock className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-blue-700 dark:text-blue-400">
                                {t('vaults.provisioningNote', 'The vault is queued as a build request. A poller picks it up within a minute and the build itself runs in the deployment account — the row shows each stage as it is reported, and says so if it stops progressing.')}
                            </span>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>{t('common.cancel', 'Cancel')}</Button>
                            <Button size="sm" onClick={() => createMut.mutate()} disabled={!createForm.description || !createForm.region || createMut.isPending}>
                                <Plus className="h-3.5 w-3.5 mr-1.5" />
                                {t('vaults.createVault', 'Create Vault')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Vault Dialog */}
            {showView && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">{t('vaults.vaultDetails', 'Vault Details')}</h3>
                        <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-[120px_1fr] gap-2">
                                <span className="text-muted-foreground">{t('vaults.vaultId', 'Vault ID')}</span>
                                <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{showView.vaultId}</code>
                                <span className="text-muted-foreground">{t('vaults.description', 'Description')}</span>
                                <span className="font-medium">{showView.description ? <TranslatableText text={showView.description} /> : '-'}</span>
                                <span className="text-muted-foreground">{t('vaults.region', 'Region')}</span>
                                <span className="font-medium">{showView.regionName || '-'}</span>
                                <span className="text-muted-foreground">{t('vaults.status', 'Status')}</span>
                                <span><StatusBadge status={showView.operationalStatus} /></span>
                                <span className="text-muted-foreground">{t('vaults.objects', 'Objects')}</span>
                                <span>{(showView.objectCount || 0).toLocaleString()}</span>
                                <span className="text-muted-foreground">{t('vaults.versions', 'Versions')}</span>
                                <span>{(showView.versionCount || 0).toLocaleString()}</span>
                                <span className="text-muted-foreground">{t('vaults.size', 'Size')}</span>
                                <span>{formatSize(showView.size)}</span>
                                <span className="text-muted-foreground">{t('vaults.retention', 'Retention')}</span>
                                <span>{showView.retentionDays != null ? t('vaults.daysValue', '{{days}} days', { days: showView.retentionDays }) : '-'}</span>
                                <span className="text-muted-foreground">{t('vaults.analytics', 'Analytics')}</span>
                                <span>{showView.analytics ? t('vaults.enabled', 'Enabled') : t('vaults.disabled', 'Disabled')}</span>
                                <span className="text-muted-foreground">{t('vaults.created', 'Created')}</span>
                                <span>{showView.created ? formatDateTime(showView.created) : '-'}</span>
                            </div>
                        </div>
                        <div className="flex justify-end mt-4">
                            <Button size="sm" onClick={() => setShowView(null)}>{t('common.close', 'Close')}</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Vault Dialog */}
            {showEdit && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-2">{t('vaults.editVault', 'Edit Vault')}</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            {showEdit.vaultId} — {showEdit.regionName}
                        </p>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm">{t('vaults.description', 'Description')}</Label>
                                <Input
                                    value={editForm.description}
                                    onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
                                    className="text-sm mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-sm">{t('vaults.retentionDaysLabel', 'Retention (days)')}</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    value={editForm.retention}
                                    onChange={(e) => setEditForm(f => ({ ...f, retention: Number(e.target.value) }))}
                                    className="text-sm mt-1"
                                />
                                <p className="text-xs text-muted-foreground mt-1">{t('vaults.retentionHint', 'Number of days to retain backups in this vault.')}</p>
                            </div>
                            <div className="flex items-center justify-between rounded-lg border p-4">
                                <div className="space-y-0.5">
                                    <Label className="text-sm font-medium">{t('vaults.analytics', 'Analytics')}</Label>
                                    <p className="text-xs text-muted-foreground">{t('vaults.analyticsDescription', 'Run analytics scanning on objects in this vault')}</p>
                                </div>
                                <Switch
                                    checked={editForm.analytics}
                                    onCheckedChange={(checked) => setEditForm(f => ({ ...f, analytics: checked }))}
                                />
                            </div>
                            {/* Network characteristics (bandwidth/latency/TCP/encryption) now live on
                                the customer Location, not the vault — edit them there. */}
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="outline" size="sm" onClick={() => setShowEdit(null)}>{t('common.cancel', 'Cancel')}</Button>
                            <Button size="sm" disabled={updateMut.isPending} onClick={() => updateMut.mutate({ id: showEdit.id, data: editForm })}>
                                {updateMut.isPending ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                                {t('vaults.saveChanges', 'Save Changes')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Setup Scheduler Dialog */}
            {showSetup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-2">{t('vaults.setupScheduler', 'Setup Scheduler')}</h3>
                        <p className="text-sm text-muted-foreground mb-1">
                            {t('vaults.vaultLabel', 'Vault')}: <span className="font-medium text-foreground"><TranslatableText text={showSetup.description} /></span>
                        </p>
                        <p className="text-sm text-muted-foreground mb-4">
                            {t('vaults.setupSchedulerDescription', 'Generate a connection token for a scheduler to access this vault.')}
                        </p>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm">{t('vaults.scheduler', 'Scheduler')}</Label>
                                <select
                                    value={selectedScheduler}
                                    onChange={(e) => setSelectedScheduler(e.target.value)}
                                    className="w-full mt-1 rounded-md border bg-background px-3 py-2 text-sm"
                                >
                                    <option value="">{t('vaults.selectScheduler', 'Select scheduler...')}</option>
                                    {schedulers.map(s => (
                                        <option key={s.schedulerId} value={s.schedulerId}><TranslatableText text={s.description} /></option>
                                    ))}
                                </select>
                                {schedulers.length === 0 && (
                                    <p className="text-xs text-muted-foreground mt-1">{t('vaults.noActiveSchedulers', 'No active schedulers available. Register a scheduler first.')}</p>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="outline" size="sm" onClick={() => setShowSetup(null)}>{t('common.cancel', 'Cancel')}</Button>
                            <Button
                                size="sm"
                                disabled={!selectedScheduler || setupMut.isPending}
                                onClick={() => setupMut.mutate({ vaultId: showSetup.id, schedulerId: selectedScheduler })}
                            >
                                <Settings className="h-3.5 w-3.5 mr-1.5" />
                                {t('vaults.generateToken', 'Generate Token')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Vault Connection Token Display */}
            {showToken && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-lg w-full mx-4">
                        <h3 className="text-lg font-semibold mb-2">{t('vaults.connectionTokenTitle', 'Vault Connection Token')}</h3>
                        <p className="text-sm text-muted-foreground mb-1">
                            {t('vaults.vaultLabel', 'Vault')}: <span className="font-medium text-foreground">{showToken.vaultDescription}</span>
                        </p>
                        <p className="text-sm text-muted-foreground mb-4">
                            {t('vaults.scheduler', 'Scheduler')}: <span className="font-medium text-foreground">{showToken.schedulerDescription}</span>
                        </p>
                        <div>
                            <Label className="text-xs text-muted-foreground">{t('vaults.connectionToken', 'Connection Token')}</Label>
                            <textarea
                                readOnly
                                value={showToken.vaultConnectionToken}
                                className="w-full h-24 mt-1 rounded-md border bg-muted px-3 py-2 font-mono text-[10px] resize-none select-all"
                            />
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                            <Button variant="outline" size="sm" onClick={() => {
                                copyToClipboard(showToken.vaultConnectionToken)
                                setCopied(true)
                                setTokenSaved(true)
                            }}>
                                {copied ? <Check className="h-3.5 w-3.5 mr-1.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
                                {copied ? t('common.copied', 'Copied') : t('vaults.copyToClipboard', 'Copy to Clipboard')}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => {
                                const subject = encodeURIComponent(
                                    t('vaults.emailSubject', 'VaultSentinel Vault Connection Token — {{vault}}', { vault: showToken.vaultDescription })
                                )
                                const body = encodeURIComponent(
                                    t(
                                        'vaults.emailBody',
                                        'Please add this vault connection to the scheduler (Settings → Vault Connections).\n\nVault: {{vault}}\nScheduler: {{scheduler}}\n\nToken:\n{{token}}\n\nThis token contains the vault configuration needed for the scheduler to upload backups.',
                                        {
                                            vault: showToken.vaultDescription,
                                            scheduler: showToken.schedulerDescription,
                                            token: showToken.vaultConnectionToken,
                                        }
                                    )
                                )
                                window.open(`mailto:?subject=${subject}&body=${body}`)
                                setTokenSaved(true)
                            }}>
                                <Mail className="h-3.5 w-3.5 mr-1.5" />{t('vaults.emailToEngineer', 'Email to Engineer')}
                            </Button>
                        </div>
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 mt-4">
                            <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-yellow-700 dark:text-yellow-400">
                                {t('vaults.tokenWarning', 'This token contains the vault connection details for the scheduler. Copy or email it now — it will not be shown again.')}
                            </span>
                        </div>
                        <div className="flex justify-end mt-4">
                            <Button size="sm" onClick={() => {
                                if (!tokenSaved) {
                                    setShowTokenWarning(true)
                                } else {
                                    setShowToken(null); setCopied(false); setTokenSaved(false); setShowTokenWarning(false)
                                }
                            }}>{t('vaults.done', 'Done')}</Button>
                        </div>
                        {showTokenWarning && (
                            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 mt-3">
                                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-red-700 dark:text-red-400">{t('vaults.tokenNotSaved', 'Token not saved')}</p>
                                    <p className="text-xs text-red-600 dark:text-red-400/80 mt-0.5">
                                        {t('vaults.tokenNotSavedDescription', "You haven't copied the token or emailed it. This token will not be available after closing this window. Are you sure?")}
                                    </p>
                                    <div className="flex gap-2 mt-2">
                                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowTokenWarning(false)}>
                                            {t('vaults.goBack', 'Go Back')}
                                        </Button>
                                        <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={() => {
                                            setShowToken(null); setCopied(false); setTokenSaved(false); setShowTokenWarning(false)
                                        }}>
                                            {t('vaults.closeAnyway', 'Close Anyway')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
